import PropostaEditor from './PropostaEditor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BASE } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useEquipes } from '../context/EquipesContext';
import { useToast } from '../context/ToastContext';
import { GraficoBarras, GraficoLinha, GraficoPizza } from './GraficosGestao';
import '../assets/css/GestaoModulo.css';
import '../assets/css/PropostasEfetivas.css';
import '../assets/css/PropostasEfetivasPolish.css';

// Mesmo traço dos ícones da Visão do Diretor: stroke 1.8, viewBox 24.
function Icon({ name, size = 18 }) {
  const paths = {
    proposta: <><path d="M6 2h9l4 4v16H6z" /><path d="M14 2v5h5M9 12h7M9 16h7" /></>,
    dinheiro: <><path d="M4 19V5M4 19h16" /><path d="m7 15 4-4 3 2 5-7" /></>,
    relogio: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    alerta: <><path d="M12 3 2.8 20h18.4z" /><path d="M12 9v5M12 17.5v.1" /></>,
    filtro: <path d="M4 6h16M7 12h10M10 18h4" />,
  };
  return (
    <svg className="pe-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>
  );
}

const moeda = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }));
// Aceita data pura ("2026-08-10", que vira meia-noite local para não recuar um dia) e
// datetime ISO com fuso ("...Z", de `created_at`), que o navegador converte sozinho.
const dataBR = (v) => {
  if (!v) return '—';
  const d = String(v).length > 10 ? new Date(v) : new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const CORES_SITUACAO = {
  'Em analise': '#6d7ce0',
  'Em análise': '#6d7ce0',
  Contraproposta: '#c07a11',
  Aceita: '#1b8a5a',
  Vendido: '#0f6247',
  Recusada: '#b03040',
  Cancelada: '#aeb4c2',
};

// Máscara de moeda em services/moeda.js — compartilhada com o Lançar Imóvel.

export default function PropostasEfetivas() {
  const toast = useToast();
  const { idCorretor } = useAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [filtros, setFiltros] = useState({
    situacao: '', forma_pagamento: '', busca: '',
    fechamento_7_dias: '', probabilidade_fechamento: '',
    // Recortes acrescentados em 25/08/2026. `team` e `id_gerente` só têm efeito para
    // perfil global — o servidor ignora para gerente e corretor, cujo escopo sai do
    // cadastro.
    team: '', bairro: '', tipo: '',
    valor_min: '', valor_max: '', sem_acao_min: '',
  });
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [propostaEditando, setPropostaEditando] = useState(null);
  const [formAberto, setFormAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [novaAcao, setNovaAcao] = useState({ descricao: '', situacao: '' });
  const { equipesOpcoes } = useEquipes();
  const podeEditar = !!dados?.escopo?.edita;
  // Só quem enxerga tudo escolhe equipe ou gerente. Para os demais o filtro nem aparece:
  // o servidor o ignoraria, e um controle que não faz nada é pior que controle ausente.
  const veTudo = !!dados?.escopo?.ve_tudo;
  const filtrosLigados = ['team', 'bairro', 'tipo', 'valor_min',
    'valor_max', 'sem_acao_min', 'fechamento_7_dias', 'probabilidade_fechamento'].filter((k) => String(filtros[k] || '').trim()).length;

  const opcoes = useMemo(
    () => dados?.opcoes || { situacoes: [], formas_pagamento: [], bairros: [], tipos: [] },
    [dados?.opcoes],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const qs = new URLSearchParams({ solicitante_id: idCorretor || '' });
      Object.entries(filtros).forEach(([k, v]) => { if (v && v !== 'false') qs.set(k, v); });
      const r = await fetch(`${BASE}/propostas?${qs.toString()}`);
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao carregar propostas');
      setDados(d);
    } catch (e) {
      toast(e.message || 'Erro ao carregar propostas', 'error');
    } finally {
      setCarregando(false);
    }
  }, [idCorretor, filtros, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNova = () => { setPropostaEditando(null); setFormAberto(true); };
  const abrirEdicao = (item) => { setPropostaEditando(item); setFormAberto(true); };

  const abrirDetalhe = async (id) => {
    try {
      const r = await fetch(`${BASE}/propostas/${id}?solicitante_id=${idCorretor}`);
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao abrir proposta');
      setDetalhe(d.proposta);
      setNovaAcao({ descricao: '', situacao: '' });
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const registrarAcao = async () => {
    if (!novaAcao.descricao.trim()) { toast('Descreva a ação', 'error'); return; }
    try {
      const r = await fetch(`${BASE}/propostas/${detalhe.id}/acoes?solicitante_id=${idCorretor}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(novaAcao),
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao registrar ação');
      toast('Ação registrada!', 'success');
      abrirDetalhe(detalhe.id);
      carregar();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Excluir a proposta do imóvel ${item.codigo_imovel || item.imovel_endereco}?`)) return;
    try {
      const r = await fetch(`${BASE}/propostas/${item.id}?solicitante_id=${idCorretor}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao excluir');
      toast('Proposta excluída.', 'success');
      carregar();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const itens = useMemo(() => dados?.itens || [], [dados?.itens]);
  const paradas = dados?.paradas || [];
  const resumo = useMemo(() => dados?.resumo || {}, [dados?.resumo]);
  const setF = (k) => (e) => setFiltros((p) => ({ ...p, [k]: e.target.value }));

  const cards = useMemo(() => ([
    { label: 'Propostas abertas', valor: resumo.abertas ?? 0, nota: `${resumo.total ?? 0} no total`, icone: 'proposta' },
    { label: 'Valor em aberto', valor: moeda(resumo.valor_aberto || 0), nota: 'Soma das propostas não fechadas', icone: 'dinheiro' },
    { label: `Sem ação há ${opcoes.dias_atencao || 7}+ dias`, valor: resumo.sem_acao_7 ?? 0, nota: 'Precisam de follow-up', tom: 'atencao', icone: 'relogio' },
    { label: `Sem ação há ${opcoes.dias_critico || 14}+ dias`, valor: resumo.sem_acao_14 ?? 0, nota: 'Estão paradas', tom: 'critico', icone: 'alerta' },
  ]), [resumo, opcoes]);

  // Distribuição por situação: barra única que resume o pipeline sem pedir outra chamada.
  const distribuicao = useMemo(() => {
    const contagem = {};
    itens.forEach((i) => { contagem[i.situacao] = (contagem[i.situacao] || 0) + 1; });
    const total = itens.length || 1;
    return (opcoes.situacoes || [])
      .map((s) => ({ ...s, qtd: contagem[s.value] || 0, pct: ((contagem[s.value] || 0) / total) * 100 }))
      .filter((s) => s.qtd > 0);
  }, [itens, opcoes.situacoes]);

  // Os graficos usam exatamente os itens devolvidos pela API. Assim, respeitam tanto
  // o escopo do usuario quanto situacao, pagamento, busca e "so as abertas".
  const graficos = useMemo(() => {
    const porSituacao = new Map();
    const porMes = new Map();
    const porResponsavel = new Map();

    itens.forEach((item) => {
      const situacao = item.situacao_label || item.situacao || 'Nao informada';
      porSituacao.set(situacao, (porSituacao.get(situacao) || 0) + 1);

      // created_at representa o lancamento no sistema; data_proposta pode ser retroativa.
      const mes = String(item.created_at || '').slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(mes)) porMes.set(mes, (porMes.get(mes) || 0) + 1);

      const responsavel = item.corretor_nome || item.gerente_nome || 'Sem responsavel';
      porResponsavel.set(responsavel, (porResponsavel.get(responsavel) || 0) + 1);
    });

    const situacoes = Array.from(porSituacao, ([rotulo, total]) => ({ rotulo, total }))
      .sort((a, b) => b.total - a.total || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));

    const mesesComDados = Array.from(porMes.keys()).sort();
    const evolucao = [];
    if (mesesComDados.length) {
      const [anoFinal, mesFinal] = mesesComDados[mesesComDados.length - 1].split('-').map(Number);
      // Doze meses continuos deixam lacunas explicitas como zero, em vez de ligar meses distantes.
      for (let delta = 11; delta >= 0; delta -= 1) {
        const data = new Date(anoFinal, mesFinal - 1 - delta, 1);
        const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        evolucao.push({
          data: chave,
          label: `${String(data.getMonth() + 1).padStart(2, '0')}/${String(data.getFullYear()).slice(-2)}`,
          total: porMes.get(chave) || 0,
        });
      }
    }

    const responsaveis = Array.from(porResponsavel, ([rotulo, total]) => ({ rotulo, total }))
      .sort((a, b) => b.total - a.total || a.rotulo.localeCompare(b.rotulo, 'pt-BR'))
      .slice(0, 8);

    return { situacoes, evolucao, responsaveis };
  }, [itens]);

  return (
    <div className="pe-page">
      <header className="pe-hero">
        <div>
          <span className="pe-eyebrow"><i /> Comercial · Propostas</span>
          <h1>Gestão de propostas</h1>
          <p>Propostas formais de compra por imóvel — situação, forma de pagamento e o que já foi feito em cada uma.</p>
        </div>
        <div className="pe-hero-lado">
          <span className="pe-hero-status">{carregando ? 'Atualizando…' : `${itens.length} proposta(s) no filtro atual`}</span>
          {podeEditar && <button type="button" className="pe-cta" onClick={abrirNova}>+ Lançar proposta</button>}
        </div>
      </header>

      {!podeEditar && !carregando && (
        <p className="pe-aviso">Seu perfil acompanha as propostas em modo leitura — quem altera é gerente, diretor ou administrativo.</p>
      )}

      <div className="pe-secao"><div><span>01 · PIPELINE</span><h2>Onde as propostas estão</h2></div><p>Volume aberto, dinheiro em jogo e o que já passou do prazo de follow-up.</p></div>

      <section className="pe-cards">
        {cards.map((c) => (
          <article key={c.label} className={`pe-card ${c.tom || ''}`}>
            <div className={`pe-card-icone ${c.tom || ''}`}><Icon name={c.icone} /></div>
            <span>{c.label}</span><strong>{c.valor}</strong><small>{c.nota}</small>
          </article>
        ))}
      </section>

      {distribuicao.length > 0 && (
        <section className="pe-distribuicao">
          <div className="pe-distribuicao-head"><strong>Distribuição por situação</strong><span>{itens.length} proposta(s)</span></div>
          <div className="pe-distribuicao-barra">
            {distribuicao.map((s) => <i key={s.value} className={`s-${s.value}`} style={{ width: `${s.pct}%` }} title={`${s.label}: ${s.qtd}`} />)}
          </div>
          <div className="pe-distribuicao-legenda">
            {distribuicao.map((s) => <span key={s.value}><i className={`s-${s.value}`} />{s.label}<b>{s.qtd}</b></span>)}
          </div>
        </section>
      )}

      {itens.length > 0 && (
        <>
          <div className="pe-secao"><div><span>02 · ANÁLISE</span><h2>Leitura da carteira</h2></div><p>Os gráficos acompanham o mesmo escopo e os mesmos filtros da lista de propostas.</p></div>
          <section className="pe-dashboard gm gm-dashboard" aria-label="Gráficos das propostas filtradas">
            <article className="gm-grafico gm-grafico--linha">
              <header>
                <div><h4>Evolução de propostas</h4><p>Lançamentos por mês nos últimos 12 meses</p></div>
                <strong className="gm-grafico-total">{itens.length}</strong>
              </header>
              <GraficoLinha pontos={graficos.evolucao} rotuloAria="Evolução mensal das propostas filtradas" unidade="proposta(s)" />
            </article>

            <article className="gm-grafico">
              <header><div><h4>Propostas por situação</h4><p>Participação de cada etapa no filtro atual</p></div></header>
              <GraficoPizza
                dados={graficos.situacoes}
                centroValor={itens.length}
                centroRotulo="propostas"
                cores={CORES_SITUACAO}
              />
            </article>

            <article className="gm-grafico">
              <header><div><h4>Volume por responsável</h4><p>Até 8 responsáveis com mais propostas no filtro</p></div></header>
              <GraficoBarras dados={graficos.responsaveis} sufixo="proposta(s)" />
            </article>
          </section>
        </>
      )}

      {paradas.length > 0 && (
        <>
          <div className="pe-secao"><div><span>03 · ACOMPANHAMENTO</span><h2>Paradas há mais tempo</h2></div><p>Ordenado pelo tempo sem ação do gerente. É por aqui que o acompanhamento começa.</p></div>
          <section className="pe-paradas">
            <div className="pe-parada-list">
              {paradas.slice(0, 8).map((item) => (
                <button type="button" key={item.id} className={`pe-parada ${item.alerta || ''}`} onClick={() => abrirDetalhe(item.id)}>
                  <div>
                    <strong>{item.codigo_imovel ? `#${item.codigo_imovel}` : 'Sem código'} · {item.bairro || 'Bairro não informado'}</strong>
                    <small>{item.corretor_nome || item.gerente_nome || '—'} · {item.situacao_label}</small>
                  </div>
                  <div className="pe-parada-num">
                    <b>{item.dias_sem_acao ?? '—'}</b><span>dias sem ação</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      <div className="pe-secao"><div><span>04 · CARTEIRA</span><h2>Todas as propostas</h2></div><p>Clique em qualquer linha para ver o histórico de ações e registrar uma nova.</p></div>

      <section className="pe-filtros">
        <span className="pe-filtro-icone"><Icon name="filtro" size={15} /></span>
        <input placeholder="Buscar por código, endereço, bairro, cliente ou corretor" value={filtros.busca} onChange={setF('busca')} />
        <select value={filtros.situacao} onChange={setF('situacao')}>
          <option value="">Todas as situações</option>
          {opcoes.situacoes.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filtros.forma_pagamento} onChange={setF('forma_pagamento')}>
          <option value="">Toda forma de pagamento</option>
          {opcoes.formas_pagamento.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        {/* O checkbox "Só as abertas" saiu: virou a opção "Ativas" do dropdown acima,
            que é o mesmo recorte (não vendida nem cancelada). Com os dois, marcar o
            checkbox e escolher "Vendido" deixava a tela pedindo duas coisas
            contraditórias, e vencia quem aplicasse por último no servidor. */}
        <button type="button" className={`pe-mais-filtros ${maisFiltros || filtrosLigados ? 'is-ativo' : ''}`}
          aria-pressed={maisFiltros} onClick={() => setMaisFiltros((v) => !v)}>
          Filtros{filtrosLigados ? ` (${filtrosLigados})` : ''}
        </button>
      </section>

      {maisFiltros && (
        <section className="pe-filtros-extra">
          {veTudo && (
            <>
              {/* Um dropdown só: `id_gerente` da proposta guarda o id da EQUIPE, o mesmo
                  valor de `team`. Dois controles para o mesmo recorte só criariam a
                  dúvida de qual usar. */}
              <label>Equipe / gerente
                <select value={filtros.team} onChange={setF('team')}>
                  <option value="">Todas as equipes</option>
                  {equipesOpcoes.map((eq) => (
                    <option key={eq.value} value={eq.value}>{eq.label}</option>
                  ))}
                </select>
              </label>
            </>
          )}
          {/* Só o que existe nas propostas do escopo — as listas vêm do servidor já
              deduplicadas por caixa ("Apartamento" e "APARTAMENTO" viram uma opção só). */}
          <label>Fechamento nos próximos 7 dias
            <select value={filtros.fechamento_7_dias} onChange={setF('fechamento_7_dias')}>
              <option value="">Todas as propostas</option>
              <option value="true">Marcadas pelo gerente</option>
            </select>
          </label>
          <label>Probabilidade de fechamento
            <select value={filtros.probabilidade_fechamento} onChange={setF('probabilidade_fechamento')}>
              <option value="">Todas as probabilidades</option>
              {(opcoes.probabilidades_fechamento || []).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label>Bairro
            <select value={filtros.bairro} onChange={setF('bairro')}>
              <option value="">Todos os bairros</option>
              {(opcoes.bairros || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label>Tipo
            <select value={filtros.tipo} onChange={setF('tipo')}>
              <option value="">Todos os tipos</option>
              {(opcoes.tipos || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Valor de
            <input type="number" value={filtros.valor_min} placeholder="0" onChange={setF('valor_min')} />
          </label>
          <label>Valor até
            <input type="number" value={filtros.valor_max} placeholder="sem teto" onChange={setF('valor_max')} />
          </label>
          <label>Parada há (dias)
            <input type="number" min="0" value={filtros.sem_acao_min} placeholder="ex.: 7"
              onChange={setF('sem_acao_min')} />
          </label>
          <button type="button" className="pe-limpar" onClick={() => setFiltros((p) => ({
            ...p, team: '', bairro: '', tipo: '', fechamento_7_dias: '', probabilidade_fechamento: '',
            valor_min: '', valor_max: '', sem_acao_min: '',
          }))}>
            Limpar
          </button>
        </section>
      )}

      <section className="pe-tabela-wrap">
        <table className="pe-tabela">
          <thead>
            <tr>
              <th>Imóvel</th><th>Bairro / Tipo</th><th>Valor</th><th>Pagamento</th>
              <th>Situação</th><th>Próximos 7 dias</th><th>Probabilidade</th><th>Em aberto</th><th>Sem ação</th><th>Corretor</th><th>Gerente</th><th />
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id} className={item.alerta ? `linha-${item.alerta}` : ''}>
                <td onClick={() => abrirDetalhe(item.id)}>
                  <strong>{item.codigo_imovel ? `#${item.codigo_imovel}` : '—'}</strong>
                  <span>{item.imovel_endereco || ''}{item.numero ? `, ${item.numero}` : ''}</span>
                </td>
                <td onClick={() => abrirDetalhe(item.id)}><strong>{item.bairro || '—'}</strong><span>{item.tipo || ''}</span></td>
                <td onClick={() => abrirDetalhe(item.id)}>
                  <strong>{moeda(item.valor)}</strong>
                  {item.valor_permuta ? <span>+ permuta {moeda(item.valor_permuta)}</span> : null}
                </td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.forma_pagamento_label || '—'}</td>
                <td onClick={() => abrirDetalhe(item.id)}><span className={`pe-situacao s-${item.situacao}`}>{item.situacao_label}</span></td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.fechamento_7_dias ? 'Marcada' : '—'}</td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.probabilidade_fechamento_label || 'Não informada'}</td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.dias_em_aberto == null ? '—' : `${item.dias_em_aberto} d`}</td>
                <td onClick={() => abrirDetalhe(item.id)} className={item.alerta || ''}>{item.fechada ? '—' : `${item.dias_sem_acao ?? 0} d`}</td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.corretor_nome || '—'}{item.id_visita ? <span>visita vinculada</span> : null}</td>
                <td onClick={() => abrirDetalhe(item.id)}>{item.gerente_nome || '—'}</td>
                <td className="pe-acoes-col">
                  {podeEditar && <button type="button" onClick={() => abrirEdicao(item)}>Editar</button>}
                  {podeEditar && <button type="button" className="perigo" onClick={() => excluir(item)}>Excluir</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!itens.length && !carregando && <p className="pe-vazio">Nenhuma proposta encontrada.</p>}
        {carregando && <p className="pe-vazio">Carregando…</p>}
      </section>

      {formAberto && <PropostaEditor key={propostaEditando?.id || 'nova'} proposta={propostaEditando}
        onClose={() => setFormAberto(false)} onSaved={() => { setFormAberto(false); setDetalhe(null); carregar(); }} />}

      {detalhe && (
        <div className="pe-modal-bg" onClick={() => setDetalhe(null)}>
          <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
            <header className="pe-modal-head-detalhe">
              <div>
                <span className="pe-eyebrow"><i /> Proposta efetiva</span>
                <h2>{detalhe.codigo_imovel ? `Imóvel #${detalhe.codigo_imovel}` : 'Proposta'} · {detalhe.bairro || '—'}</h2>
                <small>{detalhe.imovel_endereco || 'Endereço não informado'}{detalhe.numero ? `, ${detalhe.numero}` : ''}</small>
              </div>
              <div className="pe-modal-head-acoes">
                <span className={`pe-situacao s-${detalhe.situacao}`}>{detalhe.situacao_label}</span>
                <button type="button" onClick={() => setDetalhe(null)}>✕</button>
              </div>
            </header>

            <div className="pe-detalhe-topo">
              <div className="pe-detalhe-valor">
                <span>Valor da proposta</span>
                <strong>{moeda(detalhe.valor)}</strong>
                {detalhe.valor_permuta
                  ? <small>+ permuta {moeda(detalhe.valor_permuta)} · total {moeda((detalhe.valor || 0) + detalhe.valor_permuta)}</small>
                  : <small>{detalhe.forma_pagamento_label || 'Forma de pagamento não informada'}</small>}
              </div>
              <div className="pe-detalhe-chips">
                <span className="pe-chip"><b>{detalhe.dias_em_aberto ?? '—'}</b> dias em aberto</span>
                <span className={`pe-chip ${detalhe.fechada ? '' : detalhe.alerta || ''}`}>
                  <b>{detalhe.fechada ? '—' : detalhe.dias_sem_acao ?? 0}</b> dias sem ação
                </span>
                {detalhe.valor_permuta ? <span className="pe-chip"><b>Permuta</b> 2ª proposta</span> : null}
              </div>
            </div>

            {!detalhe.fechada && detalhe.alerta && (
              <p className={`pe-faixa-alerta ${detalhe.alerta}`}>
                <Icon name="alerta" size={14} />
                {detalhe.alerta === 'critico'
                  ? `Parada há ${detalhe.dias_sem_acao} dias — precisa de uma ação do gerente.`
                  : `Sem ação há ${detalhe.dias_sem_acao} dias.`}
              </p>
            )}

            <div className="pe-detalhe-grid">
              <div><span>Fechamento nos próximos 7 dias</span><strong>{detalhe.fechamento_7_dias ? 'Marcada pelo gerente' : 'Não marcada'}</strong></div>
              <div><span>Probabilidade de fechamento</span><strong>{detalhe.probabilidade_fechamento_label || 'Não informada'}</strong></div>
              <div><span>Proposta em</span><strong>{dataBR(detalhe.data_proposta)}</strong></div>
              {/* A data digitada quase sempre difere da de lançamento (vem retroativa), e é
                  pela de lançamento que o painel do diretor recorta o período. Mostrar as duas
                  evita o gestor comparar números que saem de datas diferentes. */}
              <div><span>Lançada em</span><strong>{dataBR(detalhe.created_at)}</strong></div>
              <div><span>Fechamento</span><strong>{dataBR(detalhe.data_fechamento)}</strong></div>
              <div><span>Corretor</span><strong>{detalhe.corretor_nome || '—'}</strong></div>
              <div><span>Gerente</span><strong>{detalhe.gerente_nome || '—'}</strong></div>
              <div><span>Cliente</span><strong>{detalhe.cliente || '—'}</strong></div>
              <div><span>Equipe</span><strong>{detalhe.team || '—'}</strong></div>
            </div>

            {detalhe.visita && (
              <div className="pe-visita-card">
                <span className="pe-visita-tag"><Icon name="relogio" size={13} /> Visita relacionada</span>
                <strong>{detalhe.visita.imovel}</strong>
                <small>
                  {dataBR(detalhe.visita.data_visita)} · {detalhe.visita.corretor}
                  {detalhe.visita.proposta_visita ? <em>proposta na visita: {detalhe.visita.proposta_visita}</em> : null}
                </small>
              </div>
            )}
            {detalhe.descricao_permuta && <p className="pe-nota"><b>Permuta:</b> {detalhe.descricao_permuta}</p>}
            {detalhe.observacao && <p className="pe-nota"><b>Observação:</b> {detalhe.observacao}</p>}

            <h3><Icon name="relogio" size={15} /> Ações <em>{(detalhe.acoes || []).length}</em></h3>
            <ul className="pe-timeline">
              {(detalhe.acoes || []).map((a) => (
                <li key={a.id}>
                  <div><strong>{a.descricao}</strong>{a.situacao_label ? <em>{a.situacao_label}</em> : null}</div>
                  <small>{a.autor_nome || '—'} · {a.created_at ? new Date(a.created_at).toLocaleString('pt-BR') : '—'}</small>
                </li>
              ))}
              {!(detalhe.acoes || []).length && <li className="pe-vazio">Nenhuma ação registrada.</li>}
            </ul>

            {podeEditar && (
              <div className="pe-nova-acao">
                <label>Registrar ação</label>
                <div>
                  <input placeholder="O que foi feito? Ex.: cliente pediu prazo até sexta" value={novaAcao.descricao} onChange={(e) => setNovaAcao((p) => ({ ...p, descricao: e.target.value }))} />
                  <select value={novaAcao.situacao} onChange={(e) => setNovaAcao((p) => ({ ...p, situacao: e.target.value }))}>
                    <option value="">Manter situação</option>
                    {opcoes.situacoes.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button type="button" className="pe-cta" onClick={registrarAcao}>Registrar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
